import { PublicKey } from '@hashgraph/sdk';
import { UpdateThresholdData } from '../types/transactions';

/**
 * Each signer with the key its account holds on Hedera now, read through
 * `readKey`, unless one was entered for it.
 */
export async function withCurrentKeys<T extends { accountId: string; accountKey?: string }>(
  signers: T[],
  readKey: (accountId: string) => Promise<string>,
): Promise<(T & { accountKey: string })[]> {
  return Promise.all(
    signers.map(async (signer) => {
      if (signer.accountKey) return { ...signer, accountKey: signer.accountKey };
      try {
        return { ...signer, accountKey: await readKey(signer.accountId) };
      } catch {
        throw new Error(`Failed to fetch public key for account ${signer.accountId}`);
      }
    }),
  );
}

/**
 * Signers whose key on Hedera is no longer the one the wallet stored. The
 * settings page re-reads every signer's key from the mirror, so for these
 * a threshold change also swaps in their new key.
 */
export function rotatedSigners(
  stored: { accountId: string; key?: string }[],
  rebuilt: { accountId: string; name?: string; accountKey?: string }[],
): { accountId: string; name?: string }[] {
  const sameKey = (a: string, b: string) => {
    try {
      return PublicKey.fromString(a).toStringDer() === PublicKey.fromString(b).toStringDer();
    } catch {
      // Not a single public key: nothing this app could put in the wallet,
      // so no swap to announce
      return true;
    }
  };
  return rebuilt.filter((signer) => {
    const before = stored.find((s) => s.accountId === signer.accountId);
    return !!before?.key && !!signer.accountKey && !sameKey(before.key, signer.accountKey);
  });
}

/**
 * The /threshold-update body. The signer list goes along whenever the key
 * changes beyond the threshold, including a rotated key: without it the
 * backend checks the schedule against the stored keys and refuses it.
 */
export function thresholdUpdateBody(
  schedule: { scheduleId: string; txId: string },
  newThreshold: number,
  signers: { accountId: string; name?: string; accountKey: string }[],
  signersChanged: boolean,
  rotated: unknown[],
): UpdateThresholdData {
  const body: UpdateThresholdData = {
    scheduleId: schedule.scheduleId,
    transactionId: schedule.txId,
    newThreshold,
  };
  if (signersChanged || rotated.length) {
    body.newSigners = signers.map((s) => ({
      accountId: s.accountId,
      publicKey: s.accountKey,
      name: s.name || '',
    }));
  }
  return body;
}
